// Policies and who has acknowledged them (FR-8.7).
//
// Every rule is in the database (20261015171000_policies.sql): only people:full
// publishes, a published version is never edited (only retired), a policy
// reaches its audience only, and an acknowledgement is yours, for the current
// version, timed by the database. Each row is one version; a new version names
// the one it supersedes, so acknowledging v1 is not acknowledging v2.
import { supabase } from './supabase';
import { write, type WriteResult } from './write';

export type Policy = {
  id: string;
  title: string;
  version: string;
  drive_link: string;
  audience: 'everyone' | 'departments';
  department_ids: string[];
  published_at: string;
};

/** A current policy addressed to me (the my_policies view), and when I acknowledged it. */
export type MyPolicy = {
  id: string;
  title: string;
  version: string;
  drive_link: string;
  published_at: string;
  acknowledged_at: string | null;
};

export type Ack = { policy_id: string; profile_id: string; acknowledged_at: string };

/** Who a policy can reach: the roster as HR reads it. */
export type Person = {
  id: string;
  full_name: string | null;
  department_id: string | null;
  role_id: string | null;
};

/**
 * The people a policy asks. Mirrors policy_addressed_to_me: active, not the
 * client role, and in one of its departments unless it is for everyone.
 * `people` is already the active roster.
 */
export const audienceOf = (p: Pick<Policy, 'audience' | 'department_ids'>, people: Person[]) =>
  people.filter(
    (x) =>
      x.role_id !== 'client' &&
      (p.audience === 'everyone' ||
        (!!x.department_id && p.department_ids.includes(x.department_id))),
  );

export const listMyPolicies = async (): Promise<MyPolicy[] | null> => {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('my_policies')
    .select('id, title, version, drive_link, published_at, acknowledged_at')
    .order('published_at', { ascending: false });
  return error ? null : ((data as MyPolicy[] | null) ?? []);
};

export const acknowledgePolicy = async (policyId: string, profileId: string) => {
  if (!supabase) return { error: null };
  const { error } = await supabase
    .from('policy_acknowledgements')
    .insert({ policy_id: policyId, profile_id: profileId });
  // Already acknowledged (another tab): that is the outcome asked for.
  return { error: error?.code === '23505' ? null : error };
};

export type HrPolicies = { policies: Policy[]; acks: Ack[]; people: Person[] };

/** Every current policy, every acknowledgement of one, and the active roster. */
export const loadHrPolicies = async (): Promise<HrPolicies | null> => {
  if (!supabase) return { policies: [], acks: [], people: [] };
  const [pol, ppl] = await Promise.all([
    supabase
      .from('policies')
      .select('id, title, version, drive_link, audience, department_ids, published_at')
      .is('superseded_at', null)
      .is('retired_at', null)
      .order('published_at', { ascending: false }),
    supabase
      .from('profiles')
      .select('id, full_name, department_id, role_id')
      .eq('status', 'active')
      .order('full_name'),
  ]);
  if (pol.error || ppl.error) return null;
  const policies = (pol.data as Policy[] | null) ?? [];
  const acks = policies.length
    ? await supabase
        .from('policy_acknowledgements')
        .select('policy_id, profile_id, acknowledged_at')
        .in(
          'policy_id',
          policies.map((p) => p.id),
        )
    : { data: [], error: null };
  if (acks.error) return null;
  return {
    policies,
    acks: (acks.data as Ack[] | null) ?? [],
    people: (ppl.data as Person[]) ?? [],
  };
};

export const publishPolicy = async (p: {
  title: string;
  version: string;
  driveLink: string;
  audience: Policy['audience'];
  departmentIds: string[];
  supersedes: string | null;
}) => {
  if (!supabase) return { error: null };
  const { error } = await supabase.from('policies').insert({
    title: p.title.trim(),
    version: p.version.trim(),
    drive_link: p.driveLink.trim(),
    audience: p.audience,
    department_ids: p.audience === 'everyone' ? [] : p.departmentIds,
    supersedes: p.supersedes,
  });
  return { error };
};

export const retirePolicy = async (id: string): Promise<WriteResult> => {
  if (!supabase) return { ok: true };
  return write(
    supabase.from('policies').update({ retired_at: new Date().toISOString() }).eq('id', id),
  );
};
